import type { CanvasElementLayout, CanvasFontFamily } from "../types/wedding";

export const canvasDefaultLayout: CanvasElementLayout = {
  x: 0,
  y: 0,
  scale: 1,
  fontFamily: "inherit"
};

export const canvasFontOptions: Array<{ value: CanvasFontFamily; label: string }> = [
  { value: "inherit", label: "Default" },
  { value: "serif", label: "Elegant Serif" },
  { value: "sans", label: "Clean Sans" },
  { value: "script", label: "Handwritten" }
];

export const canvasTargetSelector = [
  "[data-editor-field]",
  "[data-canvas-content]",
  "h1", "h2", "h3", "h4", "p", "a", "button", "img", "video", "blockquote"
].join(",");

const fontStacks: Record<CanvasFontFamily, string> = {
  inherit: "inherit",
  serif: "Georgia, 'Times New Roman', serif",
  sans: "Inter, ui-sans-serif, system-ui, sans-serif",
  script: "'Brush Script MT', 'Segoe Script', cursive"
};

const round = (value: number) => Math.round(value * 1000) / 1000;
const clamp = (value: number, minimum: number, maximum: number) => Math.min(maximum, Math.max(minimum, value));

export function sanitizeCanvasLayout(layout?: Partial<CanvasElementLayout> | null): CanvasElementLayout {
  const x = Number.isFinite(layout?.x) ? Number(layout?.x) : 0;
  const y = Number.isFinite(layout?.y) ? Number(layout?.y) : 0;
  const scale = Number.isFinite(layout?.scale) ? Number(layout?.scale) : 1;
  const fontFamily = layout?.fontFamily && layout.fontFamily in fontStacks ? layout.fontFamily : "inherit";
  const result: CanvasElementLayout = {
    x: round(clamp(x, -100, 100)),
    y: round(clamp(y, -100, 100)),
    scale: round(clamp(scale, 0.5, 2.5)),
    fontFamily
  };
  if (Number.isFinite(layout?.width)) result.width = round(clamp(Number(layout?.width), 5, 100));
  return result;
}

/** Desktop edits remain exact; smaller screens progressively soften offsets and scaling. */
export function responsiveCanvasFactor(viewportWidth: number) {
  if (viewportWidth >= 1024) return 1;
  if (viewportWidth >= 600) return 0.78;
  return 0.58;
}

export function responsiveCanvasLayout(layout: CanvasElementLayout, viewportWidth: number): CanvasElementLayout {
  const safe = sanitizeCanvasLayout(layout);
  const factor = responsiveCanvasFactor(viewportWidth);
  const result: CanvasElementLayout = {
    x: round(safe.x * factor),
    y: round(safe.y * factor),
    scale: round(1 + (safe.scale - 1) * factor),
    fontFamily: safe.fontFamily
  };
  if (safe.width !== undefined) result.width = round(clamp(safe.width, viewportWidth < 1024 ? 12 : 5, viewportWidth < 1024 ? 94 : 100));
  return result;
}

export function fitResponsiveCanvasScale(scale: number, naturalWidth: number, viewportWidth: number) {
  if (viewportWidth >= 1024 || naturalWidth <= 0) return scale;
  return round(Math.max(0.5, Math.min(scale, (viewportWidth - 24) / naturalWidth)));
}

export function canvasLayoutStyle(layout: CanvasElementLayout, viewportWidth: number, naturalWidth = 0) {
  const responsive = responsiveCanvasLayout(layout, viewportWidth);
  const contentWidth = responsive.width === undefined ? naturalWidth : viewportWidth * responsive.width / 100;
  const result: Record<string, string> = {
    "--canvas-x": `${responsive.x}vw`,
    "--canvas-y": `${responsive.y}vh`,
    "--canvas-scale": String(fitResponsiveCanvasScale(responsive.scale, contentWidth, viewportWidth)),
    "--canvas-font": fontStacks[responsive.fontFamily]
  };
  if (responsive.width !== undefined) result["--canvas-width"] = `${responsive.width}vw`;
  return result;
}

export function applyCanvasLayout(element: HTMLElement, layout: CanvasElementLayout | undefined, viewportWidth: number) {
  element.classList.remove("canvas-layout-applied");
  element.classList.remove("canvas-layout-width");
  const naturalWidth = layout ? element.getBoundingClientRect().width : 0;
  const style = layout ? canvasLayoutStyle(layout, viewportWidth, naturalWidth) : null;
  for (const property of ["--canvas-x", "--canvas-y", "--canvas-scale", "--canvas-font", "--canvas-width"] as const) {
    if (style?.[property]) element.style.setProperty(property, style[property]);
    else element.style.removeProperty(property);
  }
  if (!layout) return;
  if (style?.["--canvas-width"]) element.classList.add("canvas-layout-width");
  element.classList.add("canvas-layout-applied");
  if (viewportWidth >= 1024 || naturalWidth <= 0) return;
  const rect = element.getBoundingClientRect();
  const safeEdge = 12;
  let correction = 0;
  if (rect.left < safeEdge) correction = safeEdge - rect.left;
  else if (rect.right > viewportWidth - safeEdge) correction = viewportWidth - safeEdge - rect.right;
  if (correction) element.style.setProperty("--canvas-x", `calc(${style!["--canvas-x"]} + ${round(correction)}px)`);
}

function slugPart(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "content";
}

/** Assigns deterministic identities based on section, field, tag, and DOM occurrence. */
export function registerCanvasTargets(root: HTMLElement) {
  const counters = new Map<string, number>();
  const targets = Array.from(root.querySelectorAll<HTMLElement>(canvasTargetSelector))
    .filter((element) => !element.closest("[data-canvas-ui]") && element !== root);

  for (const element of targets) {
    const section = element.closest<HTMLElement>("[data-editor-section]")?.dataset.editorSection || "page";
    const identity = element.dataset.editorField
      ? `field-${slugPart(element.dataset.editorField)}`
      : `content-${slugPart(section)}-${element.tagName.toLowerCase()}`;
    const occurrence = counters.get(identity) || 0;
    counters.set(identity, occurrence + 1);
    element.dataset.canvasId = `${identity}-${occurrence}`;
    element.classList.add("canvas-content-target");
  }
  return targets;
}
